import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import NoAccessChip from 'ui-library/chips/NoAccessChip';

import {TestOrderChipGraphQL} from '../../../../graphql-fragments/sample_tests/__generated/TestOrderChipGraphQL';
import {TestOrderListingGraphQL} from '../../../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';
import {TestOrdersViewTableTestOrderFragment as TestOrderGQL} from '../test-orders/__generated/TestOrdersViewTableTestOrderFragment';

const MAX_CHIPS_IN_LIST = 3;

/**
 * If useNaText is true, will render a gray "N/A" text. Otherwise, will render null. If using within a chip
 * popover contents context, null values will result in the field not being shown at all.
 */
function renderNATextOrNull(useNaText: boolean): JSX.Element | null {
  return useNaText ? <span className={css(styles.naText)}>N/A</span> : null;
}

export function renderTestOrderContainerChip(
  item: TestOrderListingGraphQL | TestOrderGQL | TestOrderChipGraphQL,
  chipSize: ChipSize,
  {useNAText = false}: {useNAText?: boolean} = {}
): null | JSX.Element {
  if (item.container == null) {
    return renderNATextOrNull(useNAText);
  }

  return <BenchChip id={getAccessibleOrInaccessibleId(item.container)} size={chipSize} />;
}

export function renderTestOrderTestDefinitionChip(
  item: TestOrderListingGraphQL | TestOrderGQL | TestOrderChipGraphQL,
  chipSize: ChipSize
): JSX.Element {
  return <BenchChip id={getAccessibleOrInaccessibleId(item.testDefinitionCorrect)} size={chipSize} />;
}

export function renderTestOrderSample(item: TestOrderListingGraphQL | TestOrderChipGraphQL): JSX.Element {
  return <BenchChip id={item.sample.id} size={ChipSize.MINI} />;
}

export function renderTestOrderAssayChip(
  item: TestOrderListingGraphQL | TestOrderGQL | TestOrderChipGraphQL,
  chipSize: ChipSize,
  {useNAText = false}: {useNAText?: boolean} = {}
): null | JSX.Element {
  if (item.procedure == null) {
    return renderNATextOrNull(useNAText);
  }

  return <BenchChip id={getAccessibleOrInaccessibleId(item.procedure)} size={chipSize} />;
}

export function renderTestOrderWorksheetChip(
  item: TestOrderListingGraphQL | TestOrderGQL | TestOrderChipGraphQL,
  chipSize: ChipSize,
  {useNAText = false}: {useNAText?: boolean} = {}
): null | JSX.Element {
  if (item.procedureMethodExecutionInstance == null) {
    return renderNATextOrNull(useNAText);
  }
  if (isInaccessible(item.procedureMethodExecutionInstance)) {
    return <NoAccessChip size={chipSize} />;
  }

  return (
    <BenchChip
      id={getAccessibleOrInaccessibleId(item.procedureMethodExecutionInstance.worksheet)}
      size={chipSize}
    />
  );
}

export function renderTestOrderAnalysisComment(
  testOrder: TestOrderChipGraphQL,
  {useNAText = false}: {useNAText?: boolean} = {}
): JSX.Element | string | null {
  if (testOrder.analysisComment == null) {
    return renderNATextOrNull(useNAText);
  }
  return testOrder.analysisComment;
}

export function renderTestOrderDilutionFactor(
  testOrder: TestOrderChipGraphQL,
  {useNAText = false}: {useNAText?: boolean} = {}
): JSX.Element | string | null {
  if (testOrder.dilutionFactor == null) {
    return renderNATextOrNull(useNAText);
  }
  return `${testOrder.dilutionFactor}`;
}

export function renderTestOrderStudies(
  testOrder: TestOrderChipGraphQL,
  {useNAText = false}: {useNAText?: boolean} = {}
): JSX.Element | null {
  if (testOrder.studies == null || testOrder.studies.length === 0) {
    return renderNATextOrNull(useNAText);
  }
  return (
    <ChipList
      chipSize={ChipSize.MINI}
      isVertical={false}
      itemIds={testOrder.studies.map((study) => getAccessibleOrInaccessibleId(study))}
      maxChips={MAX_CHIPS_IN_LIST}
    />
  );
}

export function renderTestOrderChip(
  item: TestOrderListingGraphQL | TestOrderGQL,
  chipSize: ChipSize
): JSX.Element {
  return <BenchChip id={getAccessibleOrInaccessibleId(item)} size={chipSize} />;
}

const styles = StyleSheet.create({
  naText: {
    color: Color.GRAY_600,
  },
});
